import { Component, effect, inject, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  BranchService,
  buildPersonalIdValidation,
  ClassificationCodeService,
  LocationService,
  User,
  UserService,
  UserSourceService,
  UserSource, toCalendarDate, fromCalendarDate, splitMaskedFields, omitKeys } from '@beautyapp/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Store } from '@ngxs/store';
import { AuthState } from '@beautyapp/auth';
import { NZ_MODAL_DATA, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzFormModule } from 'ng-zorro-antd/form';
import { InputModule } from '@beautyapp/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { SelectComponent } from '../../../../../form/src/lib/select/select.component';
import { TextareaComponent } from '../../../../../form/src/lib/textarea/textarea.component';
import { DatepickerComponent } from '../../../../../form/src/lib/datepicker/datepicker.component';

@Component({
  selector: 'beautyapp-user-add-edit',
  templateUrl: './user-add-edit.component.html',
  styleUrls: ['./user-add-edit.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NzModalModule,
    NzFormModule,
    InputModule,
    SelectComponent,
    TextareaComponent,
    NzGridModule,
    NzButtonModule,
    NzIconModule,
    TranslatePipe,
    DatepickerComponent,
  ],
})
export class UserAddEditComponent implements OnInit {
  private service = inject(UserService);
  private modal = inject(NzModalRef);
  // beaut-499: the duplicate-phone warning. NzModalRef is THIS dialog;
  // opening another one needs the service.
  private modalService = inject(NzModalService);
  private _fb = inject(UntypedFormBuilder);
  private locationService = inject(LocationService);
  private store = inject(Store);
  private branchService = inject(BranchService);

  // Personal-ID (identityNumber) validation is driven by the per-branch config
  // (beaut-315): { enabled, required, minLength, maxLength }. Fetched in ngOnInit
  // and applied dynamically — do NOT hardcode "required" or "11 digits" here.
  personalIdRequired = false; // drives the field's required asterisk
  personalIdError = 'validators.personalId11Digits'; // dynamic error message
  // beaut-499: whether this branch refuses a duplicate phone outright.
  private mobileUnique = false;

  /**
   * beaut-559: the fields this viewer's branch has hidden from their role.
   *
   * They arrive as `'***'`. Left alone, the personal-ID validator rejects that
   * as "not 11 digits" and the whole record becomes unsaveable -- a phone
   * number could not be corrected because of a number the user may not read.
   * So they are blanked on load, shown as disabled, and dropped on submit.
   *
   * Template-visible: the fields are disabled and labelled from this list.
   */
  maskedFields: string[] = [];

  isHidden(field: string): boolean {
    return this.maskedFields.includes(field);
  }

  // beaut-382: in-flight guard. The Save button had no loading/disabled state
  // and submit() no re-entry check, so a rapid double-click fired two real
  // POSTs. (The second Network entry QA reported is the Angular Service Worker
  // proxying a single request — ngsw commits to every request via respondWith
  // and re-fetches uncached ones, so DevTools shows two rows for one call.)
  saving = false;

  readonly data = inject(NZ_MODAL_DATA);
  readonly classificationCodeService = inject(ClassificationCodeService);
  readonly userSourceService = inject(UserSourceService);

  get user() {
    return this.data.user;
  }

  get isAnketa() {
    return this.data.isAnketa || false;
  }

  genders = this.classificationCodeService.getGender();
  bloodGroups = this.classificationCodeService.getBloodGroup();
  smsLanguages = [
    { id: 'ka', value: 'ქართული' },
    { id: 'ru', value: 'რუსული' },
    { id: 'en', value: 'ინგლისური' },
  ];
  userSources = toSignal(this.userSourceService.dropdown(), {
    initialValue: [],
  });
  countries = toSignal(this.locationService.getCountries(), {
    initialValue: [],
  });
  cities = toSignal(this.locationService.getCities({}), { initialValue: [] });

  form: UntypedFormGroup;

  constructor() {
    this.form = this._fb.group({
      id: null,
      cardNo: null,
      firstname: [null, Validators.required],
      lastname: [null, Validators.required],
      gender: [null, Validators.required],
      mobilePhone: [null, Validators.required],
      phone: null,
      email: [null, Validators.email],
      // Validators applied dynamically from the branch personal-id config (ngOnInit).
      identityNumber: [null, []],
      birthDate: null,
      description: [null],
      countryId: null,
      cityId: null,
      address: null,
      bloodGroup: null,
      workplace: null,
      userSourceId: null,
      smsLanguage: [null],
    });

    // Default-select the pinned first city (Tbilisi) on create, not on edit.
    effect(() => {
      const list = this.cities();
      if (!this.user && list?.length && !this.form.get('cityId')?.value) {
        this.form.patchValue({ cityId: list[0].id }, { emitEvent: false });
      }
    });
  }

  ngOnInit(): void {
    if (this.user) {
      // beaut-559: a field the viewer may not see arrives as `'***'`. Patching
      // that into a control makes the record unsaveable, so blank it here and
      // drop it in `payload()` -- the stored value is then left exactly as it
      // was, which is the right outcome for a field nobody could read.
      const { visible, maskedKeys } = splitMaskedFields(this.user as Record<string, any>);
      this.maskedFields = maskedKeys;

      this.form.patchValue({
        ...visible,
        gender: +this.user.gender,
        // beaut-525: a `date` column arrives as a string; the picker needs a
        // Date or it falls back to the epoch.
        birthDate: fromCalendarDate(visible['birthDate']),
      });

      for (const key of maskedKeys) {
        this.form.get(key)?.disable({ emitEvent: false });
      }
    }

    // Make address required in anketa mode
    if (this.isAnketa) {
      this.form.get('address')?.setValidators([Validators.required]);
      this.form.get('address')?.updateValueAndValidity();
    }

    this.applyPersonalIdValidation();
  }

  /**
   * Personal-ID validation is per-branch (beaut-315): { enabled, required,
   * minLength, maxLength }. The required flag and the digit range are both
   * configurable — the old code hardcoded "required + exactly 11 digits", which
   * ignored branches configured for e.g. 6 digits or optional entry.
   */
  private applyPersonalIdValidation(): void {
    const branch = this.store.selectSnapshot(AuthState.getActiveBranch);
    if (!branch?.id) return;
    this.branchService.getPersonalIdConfig(branch.id).subscribe({
      next: (cfg) => {
        this.setPersonalIdValidators(cfg);
        // beaut-499: the same request already carries the phone rule. When
        // it is ON the server refuses a duplicate outright and the warning
        // must not also fire; when OFF, the warning is the only thing
        // standing between the operator and a second record.
        this.mobileUnique = !!cfg.mobileUnique;
      },
      // On config load failure, fall back to the branch's enabled flag +
      // the legacy 11-digit rule so we never send an unvalidated ID.
      error: () =>
        this.setPersonalIdValidators({
          enabled: !!branch?.personalIdValidationEnabled,
          required: true,
          minLength: 11,
          maxLength: 11,
        }),
    });
  }

  private setPersonalIdValidators(cfg: {
    enabled?: boolean;
    required?: boolean;
    minLength?: number;
    maxLength?: number;
  }): void {
    const { validators, required, error } = buildPersonalIdValidation(cfg);
    this.personalIdRequired = required;
    this.personalIdError = error;
    const ctrl = this.form.get('identityNumber');
    ctrl?.setValidators(validators);
    ctrl?.updateValueAndValidity();
  }

  submit() {
    // beaut-382: ignore re-entry while a save is already in flight.
    if (this.saving) {
      return;
    }
    for (const key in this.form.controls) {
      this.form.controls[key].markAsDirty();
      this.form.controls[key].markAsTouched();
      this.form.controls[key].updateValueAndValidity();
    }
    if (this.form.valid) {
      this.saving = true;
      const onSuccess = () => {
        this.saving = false;
        this.modal.triggerOk();
      };
      const onError = () => {
        // Release the guard so the user can correct the input and retry —
        // e.g. a 409 when the personal ID is already taken.
        //
        // No toast here. `httpErrorInterceptor` already raises one for every
        // 4xx that is not 401/402 and not from a module that opted out, so
        // this produced a second, identical notification beside it — two boxes
        // carrying the same sentence, differing only in title. The
        // interceptor's is the one that stays: it is the app-wide path, it
        // translates known server codes through `serverErrors.*`, and the
        // message itself now names the field and the conflicting record, which
        // is what the local title was there to supply.
        this.saving = false;
      };
      const send = () => {
        if (this.user) {
          this.service.update(this.payload()).subscribe({
            next: onSuccess,
            error: onError,
          });
        } else {
          this.service.create(this.payload()).subscribe({
            next: onSuccess,
            error: onError,
          });
        }
      };

      this.confirmPhoneDuplicate(send, () => {
        // Release the re-entry guard, or a cancelled warning leaves the form
        // unable to submit ever again.
        this.saving = false;
      });
    }
  }

  /**
   * beaut-499: warn before putting a second customer on one phone number.
   *
   * Only for the branches that have the rule OFF. With it ON the server refuses
   * the save and names the holder — asking first would put a question in front
   * of an answer that was never in doubt.
   *
   * Fails OPEN. If the lookup errors the save proceeds: this is a warning, and
   * a warning that cannot load is not a reason to stop somebody working. The
   * branches that actually want the number protected have the setting on, and
   * that path does not go through here at all.
   */
  private confirmPhoneDuplicate(proceed: () => void, cancel: () => void): void {
    const phone = (this.form.value.mobilePhone ?? '').toString().trim();

    if (this.mobileUnique || !phone) {
      proceed();

      return;
    }

    this.service.findPhoneDuplicate(phone, this.user?.id).subscribe({
      next: (res) => {
        const duplicates = res?.duplicates ?? [];
        if (!duplicates.length) {
          proceed();

          return;
        }

        // Every holder, and how many. A number held by two people is a
        // different decision from a number held by one, and naming only the
        // first found hides that — the operator agrees to one duplicate and
        // creates a third record.
        const names = duplicates
          .slice(0, 3)
          .map(
            (d) =>
              [d.firstname, d.lastname].filter(Boolean).join(' ').trim() ||
              'უსახელო ჩანაწერი'
          )
          .join(', ');
        const more = duplicates.length - Math.min(duplicates.length, 3);
        const name =
          duplicates.length === 1
            ? names
            : `${duplicates.length} ჩანაწერი: ${names}${more > 0 ? ` და კიდევ ${more}` : ''}`;

        this.modalService.confirm({
          nzTitle: 'ასეთი ნომრით მომხმარებელი უკვე არსებობს',
          nzContent: `${name} — ${phone}. გსურთ მაინც ახალი მომხმარებლის შექმნა?`,
          nzOkText: 'გაგრძელება',
          nzCancelText: 'გაუქმება',
          nzOnOk: proceed,
          nzOnCancel: cancel,
        });
      },
      error: () => proceed(),
    });
  }

  /**
   * beaut-525: the birth date leaves as a calendar date, not an instant.
   *
   * `nz-date-picker` binds a LOCAL-midnight `Date`; serialised into the body it
   * becomes `…T20:00:00Z` for a Tbilisi user, and webapp-ge — which runs UTC —
   * then resolves it to the day before. Both the create and the update path go
   * through here so neither can be fixed while the other is forgotten.
   */
  private payload(): Record<string, unknown> {
    // beaut-559: `omitKeys` on top of `form.value`, which already excludes
    // disabled controls. Belt and braces on purpose -- the day someone reaches
    // for `getRawValue()` to fix an unrelated bug, the hidden fields would
    // otherwise start travelling again, as `null` this time, which CLEARS the
    // stored value instead of merely echoing the mask.
    const values = omitKeys({ ...this.form.value }, this.maskedFields);

    // `birthDate` is re-added by hand for the date conversion, so it has to be
    // asked about by hand too. Adding it back while hidden would submit `null`
    // and wipe a date the viewer never saw.
    if (this.isHidden('birthDate')) {
      return values;
    }

    return { ...values, birthDate: toCalendarDate(this.form.value.birthDate) };
  }
}
